import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  scan,
  Subscription,
  switchMap,
} from 'rxjs';

import { AuthRestaurantService, SocketService } from '@tablesready/frontend-store';
import { ApiClientService } from '@tablesready/api-client';
import { PricePlansRestrictionsService } from '@tablesready/frontend-restrictions';
import { WebSocketEventsEnum } from '@shared/websocket';
import { Party, PartySource } from '@shared/party';
import { hasUnpaidCheckout } from '@shared/party/functions';

type LocationKey = { id: string | null; planCode: string | null; tierRank: number };

@Injectable({ providedIn: 'root' })
export class NotificationCenterService {
  private readonly unreadMessages$ = new BehaviorSubject<number>(0);
  private readonly unreadNotifications$ = new BehaviorSubject<number>(0);

  readonly messagesCount$ = this.unreadMessages$.asObservable();
  readonly notificationsCount$ = this.unreadNotifications$.asObservable();

  readonly totalUnreadCount$ = combineLatest([
    this.messagesCount$,
    this.notificationsCount$,
  ]).pipe(map(([messages, notifications]) => messages + notifications));

  private subscriptions = new Subscription();
  private isInitialized = false;

  constructor(
    private readonly socketService: SocketService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly http: ApiClientService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
  ) {}

  init(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    const initialKey: LocationKey = { id: null, planCode: null, tierRank: 0 };
    const authSub = this.authRestaurantService.selectWithAccountAndLocation()
      .pipe(
        map(user => ({
          id: user.location?._id || null,
          planCode: user.location?.recurly?.planCode || null,
          tierRank: this.pricePlansRestrictionsService.getTierRank(user.account?.subscriptionType, user.location?.recurly),
        })),
        distinctUntilChanged((a, b) => a.id === b.id && a.planCode === b.planCode),
        scan(
          (acc, curr) => ({ prev: acc.curr, curr }),
          { prev: initialKey, curr: initialKey },
        ),
        switchMap(({ prev, curr }) => {
          if (!curr.id) {
            return of({ messages: 0, notifications: 0 });
          }

          const isUpgrade = !!prev.id && prev.id === curr.id && curr.tierRank > prev.tierRank;
          if (isUpgrade) {
            // Plan upgrade - the job marks NC as read and emits allMessagesRead/allNotificationsRead.
            // Skip the load to avoid a flicker of stale counts during the transition window.
            const gainedMessagesAccess = prev.tierRank === 0;
            return of({
              messages: gainedMessagesAccess ? 0 : this.unreadMessages$.value,
              notifications: 0,
            });
          }

          return this.loadUnreadCounts(curr.id)
            .pipe(catchError(() => of({ messages: 0, notifications: 0 })));
        }),
      )
      .subscribe((data: { messages: number; notifications: number }) => {
        this.unreadMessages$.next(data.messages);
        this.unreadNotifications$.next(data.notifications);
      });
    this.subscriptions.add(authSub);

    this.listenToWebSocketEvents();
  }

  destroy(): void {
    this.isInitialized = false;
    this.unreadMessages$.next(0);
    this.unreadNotifications$.next(0);

    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
  }

  getMessages(restaurantId: string, limit: number, offset: number): Observable<Party[]> {
    return this.http
      .get(`/v.1/parties/messages/recent?restaurantId=${restaurantId}&limit=${limit}&offset=${offset}`);
  }

  getNotifications(restaurantId: string, limit: number, offset: number): Observable<Party[]> {
    return this.http
      .get(`/v.1/parties/notifications/recent?restaurantId=${restaurantId}&limit=${limit}&offset=${offset}`);
  }

  markMessageAsRead(partyId: string): Observable<{ markedAsRead: number }> {
    return this.http.post<{ markedAsRead: number }>(`/v.1/parties/messages/${partyId}/read`, {});
  }

  markNotificationAsRead(notificationId: string): Observable<unknown> {
    return this.http.post(`/v.1/parties/notifications/${notificationId}/read`, {});
  }

  markAllMessagesAsRead(restaurantId: string): Observable<{ markedAsRead: number }> {
    return this.http.post<{ markedAsRead: number }>(`/v.1/parties/messages/read-all?restaurantId=${restaurantId}`, {});
  }

  markAllNotificationsAsRead(restaurantId: string): Observable<{ markedAsRead: number }> {
    return this.http.post<{ markedAsRead: number }>(`/v.1/parties/notifications/read-all?restaurantId=${restaurantId}`, {});
  }

  extractPartyFromEvent(event: any): Party | null {
    try {
      if (Array.isArray(event) && event.length >= 2 && Array.isArray(event[1])) {
        const parties = event[1];
        if (parties.length > 0 && this.isValidParty(parties[0])) {
          return parties[0];
        }
      }

      if (Array.isArray(event) && event.length > 0 && this.isValidParty(event[0])) {
        return event[0];
      }

      if (this.isValidParty(event)) {
        return event;
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  extractPartyIdFromEvent(event: any): string | null {
    return this.extractWsPayload(event).partyId;
  }

  private extractWsPayload(event: any): { partyId: string | null; count: number } {
    try {
      if (Array.isArray(event) && event.length >= 2 && Array.isArray(event[1])) {
        return { partyId: event[1][0] || null, count: event[1][1] || 1 };
      }

      if (Array.isArray(event) && event.length > 0 && typeof event[0] === 'string') {
        return { partyId: event[0], count: event[1] || 1 };
      }

      if (typeof event === 'string') {
        return { partyId: event, count: 1 };
      }

      return { partyId: null, count: 1 };
    } catch {
      return { partyId: null, count: 1 };
    }
  }

  private loadUnreadCounts(restaurantId: string): Observable<{ messages: number; notifications: number }> {
    return this.http.get(`/v.1/parties/notification-center/unread-count?restaurantId=${restaurantId}`);
  }

  private listenToWebSocketEvents(): void {
    const inboundSub = this.socketService.getEventStream(WebSocketEventsEnum.InboundSmsEvent)
      .subscribe(() => {
        this.unreadMessages$.next(this.unreadMessages$.value + 1);
      });
    this.subscriptions.add(inboundSub);

    const partyCreatedSub = this.socketService
      .getEventStream(WebSocketEventsEnum.partyCreated)
      .subscribe((parties: Party[]) => {
        this.countNewNotifications(parties);
      });
    this.subscriptions.add(partyCreatedSub);

    // An unpaid hold is skipped above, so the badge only learns about an online booking that
    // required payment once that payment lands. Payload is the plain `[party]` array the
    // location room carries (no sequence-number envelope), i.e. the same shape as above.
    const paymentConfirmedSub = this.socketService
      .getEventStream(WebSocketEventsEnum.partyPaymentConfirmed)
      .subscribe((parties: Party[]) => {
        this.countNewNotifications(parties);
      });
    this.subscriptions.add(paymentConfirmedSub);

    const messageReadSub = this.socketService
      .getEventStream(WebSocketEventsEnum.messageRead)
      .subscribe((wsData: unknown) => {
        const { partyId, count } = this.extractWsPayload(wsData);
        if (partyId) {
          this.unreadMessages$.next(Math.max(0, this.unreadMessages$.value - count));
        }
      });
    this.subscriptions.add(messageReadSub);

    const notificationReadSub = this.socketService
      .getEventStream(WebSocketEventsEnum.notificationRead)
      .subscribe((wsData: unknown) => {
        const partyId = this.extractPartyIdFromEvent(wsData);
        if (partyId) {
          this.unreadNotifications$.next(Math.max(0, this.unreadNotifications$.value - 1));
        }
      });
    this.subscriptions.add(notificationReadSub);

    const allMessagesReadSub = this.socketService
      .getEventStream(WebSocketEventsEnum.allMessagesRead)
      .subscribe(() => {
        this.unreadMessages$.next(0);
      });
    this.subscriptions.add(allMessagesReadSub);

    const allNotificationsReadSub = this.socketService
      .getEventStream(WebSocketEventsEnum.allNotificationsRead)
      .subscribe(() => {
        this.unreadNotifications$.next(0);
      });
    this.subscriptions.add(allNotificationsReadSub);
  }

  // Shared by partyCreated and partyPaymentConfirmed: a party with an unpaid checkout does not
  // exist yet as far as the restaurant is concerned, so it never bumps the badge on creation —
  // its confirmation event does. A paid party can never match `hasUnpaidCheckout`, so the two
  // streams can safely share this filter without double counting.
  private countNewNotifications(parties: Party[]): void {
    if (!Array.isArray(parties)) {
      return;
    }

    const notificationParties = parties.filter(party => [
      PartySource.sms, PartySource.public, PartySource.api, PartySource.google,
    ].includes(party.source) && !hasUnpaidCheckout(party));

    if (notificationParties.length > 0) {
      this.unreadNotifications$.next(this.unreadNotifications$.value + notificationParties.length);
    }
  }

  private isValidParty(party: any): party is Party {
    return party &&
      typeof party === 'object' &&
      '_id' in party &&
      typeof party._id === 'string';
  }
}
